import React, {
  useContext,
} from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import Divider from '@mui/material/Divider';
import NavItem from 'pages/Dashboard/components/NavItem';
import PatientSelect from 'pages/Dashboard/pages/Todos/components/PatientSelect';
import {
  ImproveIcon,
} from 'core/icons';
import NotificationIndicator from 'views/EMR/Notifications/NotificationIndicator';
import ToolTip from 'core/components/Tooltip';
import {
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';
import CreateButton from 'views/EMR/Layout/CreateButton';
import InboxIndicator from 'views/EMR/Messages/MessageIndicator';
import ProfileMenu from 'views/EMR/Layout/ProfileMenu';
import SettingsIcon from 'core/icons/settings';
import AssistantIcon from '@mui/icons-material/AssistantOutlined';
import MedicalInformationIcon from '@mui/icons-material/MedicalServicesOutlined';
import {
  aiAssistantEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  helpDeskURL,
} from 'core/constants';
import {
  UserSettingsContext,
} from 'pages/Dashboard/contexts/userSettings';
import Skeleton from 'core/components/Skeleton';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import SETTINGS_PATHS from 'views/EMR/Settings/paths';

export default function Topbar() {
  const navigate = useNavigate();
  const {
    canPath,
    defaultSettingsPath,
    getDefaultChartPath,
    hasChartAccess,
    isRestricted: hasLimitedAccess,
    userInfo,
  } = useEMRAccess();
  const { loadingAppSettings: appIsLoading } = useContext(UserSettingsContext);
  const showGlobalPatientSearch = !hasLimitedAccess
    || hasChartAccess;
  const showSettings = canPath(defaultSettingsPath);

  return (
    <div className="flex justify-between gap-3 w-full items-center">
      <div className="flex flex-1 items-center gap-3 min-w-0">
        {!hasLimitedAccess && <CreateButton />}
        {showGlobalPatientSearch && (
          <div className="flex-1 nw-search-bar">
            <PatientSelect
              label={false}
              id="global-patient-search"
              placeholder="Enter patient name, phone or date of birth"
              disabled={appIsLoading}
              clearOnBlur
              recentSearchEnabled
              inputProps={{
                autoComplete: 'new-password',
                autoCapitalize: 'none',
                name: 'global-patient-search',
                spellCheck: false,
              }}
              onChange={(value) => {
                if (!isNilOrZero(value?.patientId)) {
                  navigate(getDefaultChartPath(value?.patientId) ?? '', { replace: true });
                }
              }}
              className="w-1/2 !mb-0 jumbo-search transition-width duration-100 ease-in-out focus-within:w-full !text-white"
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-0.5">
        {!hasLimitedAccess && userInfo?.flags?.agents?.aiAssistantAgentEnabled && (
          <ToolTip
            title="AI Assistant"
            placement="bottom"
          >
            <div>
              <NavItem
                onClick={() => {
                  aiAssistantEventChannel.emit('onShowAIAssistantDialog');
                }}
                trackLabel="ai-assistant"
                className="bg-transparent"
              >
                <AssistantIcon className="!w-5 !h-5 !text-blue-400" />
              </NavItem>
            </div>
          </ToolTip>
        )}
        <Divider
          flexItem
          orientation="vertical"
          variant="middle"
          className="bg-gray-400 !mx-2 min-h-[24px]"
        />
        {appIsLoading
          ? (
            <div className="gap-3 flex items-center">
              <Skeleton variant="rounded" className="!w-8 !h-8 !bg-gray-100" />
              <Skeleton variant="rounded" className="!w-8 !h-8 !bg-gray-100" />
              <Skeleton variant="rounded" className="!w-8 !h-8 !bg-gray-100" />
              <Skeleton variant="rounded" className="!w-8 !h-8 !bg-gray-100" />
              <Skeleton variant="rounded" className="!w-8 !h-8 !bg-gray-100" />
            </div>
          )
          : (
            <>
              {!hasLimitedAccess && (
                <ToolTip
                  title="Notifications"
                  placement="bottom"
                >
                  <div>
                    <NavItem
                      href="/messages"
                      trackLabel="messages"
                    >
                      <InboxIndicator className="!w-5 !h-5 !text-white" />
                    </NavItem>
                  </div>
                </ToolTip>
              )}
              {showSettings && (
                <ToolTip
                  title="Settings"
                  placement="bottom"
                >
                  <div>
                    <NavItem
                      href={SETTINGS_PATHS.ROOT}
                      trackLabel="settings"
                    >
                      <SettingsIcon className="!w-5 !h-5 !text-white" />
                    </NavItem>
                  </div>
                </ToolTip>
              )}
              {!hasLimitedAccess && (
                <ToolTip
                  title="Help / Requests"
                  placement="bottom"
                >
                <div className="nw-help-button">
                  <NavItem
                    href={helpDeskURL}
                    trackLabel="help-request"
                    target="_blank"
                  >
                    <ImproveIcon className="!w-5 !h-5 !text-white" />
                  </NavItem>
                </div>
              </ToolTip>
              )}
              {!hasLimitedAccess && (
                <ToolTip
                  title="DoseSpot Notifications"
                  placement="bottom"
                >
                  <div className="nw-notifications-button">
                    <NavItem
                      href="/notifications"
                      trackLabel="dosespot-notification"
                    >
                      <NotificationIndicator className="!w-5 !h-5 !text-white" />
                    </NavItem>
                  </div>
                </ToolTip>
              )}
              {!hasLimitedAccess && notEmptyString(userInfo?.organization?.ipadFullIntakeUrl) && (
                <ToolTip
                  title="Open Intake"
                  placement="bottom"
                >
                  <div>
                    <NavItem
                      href={userInfo?.organization?.ipadFullIntakeUrl ?? ''}
                      target="_blank"
                    >
                      <MedicalInformationIcon className="!w-5 !h-5 !text-white" />
                    </NavItem>
                  </div>
                </ToolTip>
              )}
              <ProfileMenu />
            </>
          )}
      </div>
    </div>
  );
}
